import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT, PAGE_CONTAINER_MAX_WIDTH, documentPageContainerSx } from '../../constants/documentTheme';
import { mainAppOrigin } from '../../utils/developerHost';
import api from '../../config/api';

/**
 * Lightweight catalog embed preview for partners testing the widget token.
 * Public Book links go to the main app package pages.
 */
const CatalogEmbed = () => {
  const { token } = useParams();
  const { t } = useTranslation();
  const [packages, setPackages] = useState([]);
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/travel-packages/catalog/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setPackages(res.data?.packages || []);
        setPartner(res.data?.partner || null);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || t('developerPortal.embed.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, t]);

  const origin = mainAppOrigin();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  return (
    <Box sx={{ ...documentPageContainerSx, maxWidth: (theme) => theme.breakpoints.values[PAGE_CONTAINER_MAX_WIDTH], mx: 'auto', py: { xs: 2, md: 3 } }}>
      <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: 28, mb: 1 }}>
        {partner?.companyName || t('developerPortal.embed.title')}
      </Typography>
      {error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Grid container spacing={2}>
          {packages.map((pkg) => (
            <Grid item xs={12} sm={6} md={4} key={pkg.id || pkg.slug}>
              <Card sx={{ borderRadius: 0, border: `1px solid ${DOCUMENT.ruleHard}`, boxShadow: 'none' }}>
                <CardContent>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 1 }}>
                    {pkg.title}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 2 }}>
                    {pkg.destination || pkg.package_type || ''}
                  </Typography>
                  <Button
                    component="a"
                    href={`${origin}/packages/${pkg.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    disableElevation
                    sx={{ bgcolor: DOCUMENT.oxblood, borderRadius: 0, textTransform: 'none' }}
                  >
                    {t('developerPortal.embed.book')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default CatalogEmbed;
